// screens/RecordPage.tsx — C1 (2026-09-28): one wiki page, whole. On
// spread.css like the radar's own three spreads — hero = title + rev/
// updated/editor line + body (MdLite, wikilinks live); aside = History,
// Linked from, Links to, Copy slug. A flagged page carries the one act
// this surface has: Mark read.
import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  getRecordPage,
  getRecordPageRevisions,
  getRecordReadingList,
  markRecordFlagRead,
  type RecordNoteRevisionApi,
  type RecordPageApi,
  type RecordReadingListItemApi,
} from '../lib/api';
import { BackLink } from '../components/BackLink';
import { MdLite } from '../components/MdLite';
import './spread.css';
import './RecordPage.css';

export interface RecordPageProps {
  tenantId: string;
  slug: string;
  onBack: () => void;
  onOpenPage: (slug: string) => void;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; page: RecordPageApi; revisions: RecordNoteRevisionApi[]; flag: RecordReadingListItemApi | null };

const HISTORY_FOLD = 10;

export function RecordPage({ tenantId, slug, onBack, onOpenPage }: RecordPageProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [copyReceipt, setCopyReceipt] = useState<string | null>(null);
  const [markBusy, setMarkBusy] = useState(false);
  const [markReceipt, setMarkReceipt] = useState<string | null>(null);

  function refresh() {
    return Promise.all([getRecordPage(tenantId, slug), getRecordPageRevisions(tenantId, slug, 50), getRecordReadingList(tenantId)])
      .then(([page, revisions, reading]) => {
        const flag = reading.find((r) => r.kind === 'note' && r.slug === slug && !r.readAt) ?? null;
        setLoad({ phase: 'ready', page, revisions, flag });
      })
      .catch((err: unknown) => {
        setLoad({ phase: 'error', message: err instanceof ApiError ? err.message : 'the record is not reachable' });
      });
  }

  useEffect(() => {
    setLoad({ phase: 'loading' });
    setHistoryOpen(false);
    setCopyReceipt(null);
    setMarkReceipt(null);
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, slug]);

  async function copySlug() {
    try {
      await navigator.clipboard.writeText(slug);
      setCopyReceipt(`copied · ${slug}`);
    } catch {
      setCopyReceipt('could not copy — the clipboard refused');
    }
  }

  async function markRead(flag: RecordReadingListItemApi) {
    setMarkBusy(true);
    try {
      const r = await markRecordFlagRead(tenantId, flag.flagId);
      setMarkReceipt(r.receipt);
      if (load.phase === 'ready') setLoad({ ...load, flag: null });
    } catch (err) {
      setMarkReceipt(err instanceof ApiError ? err.message : 'not recorded');
    } finally {
      setMarkBusy(false);
    }
  }

  if (load.phase === 'loading') {
    return (
      <div class="sp">
        <BackLink label="Record" onClick={onBack} />
        <div class="sp__say">One moment…</div>
      </div>
    );
  }
  if (load.phase === 'error') {
    return (
      <div class="sp">
        <BackLink label="Record" onClick={onBack} />
        <div class="sp__say">
          The record is not reachable.
          <br />
          <span class="sp__say-soft">{load.message}</span>
        </div>
      </div>
    );
  }

  const { page, revisions, flag } = load;
  const lastEditor = revisions[0]?.editedBy;
  const visibleHistory = historyOpen ? revisions : revisions.slice(0, HISTORY_FOLD);

  return (
    <div class="sp">
      <BackLink label="Record" onClick={onBack} />
      <h1 class="sp__title">{page.note.title}</h1>
      <p class="rpage__meta">
        rev {page.revisionCount} · updated {new Date(page.note.updatedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
        {lastEditor ? ` · by ${lastEditor}` : ''}
        {page.note.noteType ? <span class="sp__chip" style={{ marginLeft: '8px' }}>{page.note.noteType}</span> : null}
      </p>

      {flag ? (
        <div class="rpage__flag">
          <span>
            flagged for you by {flag.flaggedBy}
            {flag.why ? ` · ${flag.why}` : ''}
          </span>
          <button type="button" class="sp__act" disabled={markBusy} onClick={() => void markRead(flag)}>
            Mark read
          </button>
        </div>
      ) : markReceipt ? (
        <div class="rpage__flag">
          <span>{markReceipt}</span>
        </div>
      ) : null}

      <div class="sp__grid">
        <div class="sp__hero">
          <MdLite md={page.note.bodyMd} wikilinks={{ outlinks: page.outlinks, unresolvedLinks: page.unresolvedLinks, onOpenPage }} />
        </div>

        <div class="sp__aside">
          <button type="button" class="rpage__fold-toggle" onClick={() => setHistoryOpen((o) => !o)}>
            <span class="sp__sec">
              <span>History · {revisions.length}</span>
            </span>
          </button>
          {revisions.length === 0 ? (
            <div class="sp__empty">No revisions yet.</div>
          ) : (
            <>
              {visibleHistory.map((r) => (
                <div class="sp__row" key={r.revision} style={{ cursor: 'default' }}>
                  <span class="sp__row-top">
                    <span class="sp__row-name">rev {r.revision} · {r.editedBy}</span>
                  </span>
                  <span class="sp__row-sub">{new Date(r.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                  {r.editSummary ? <span class="sp__row-sub">{r.editSummary}</span> : null}
                </div>
              ))}
              {!historyOpen && revisions.length > HISTORY_FOLD ? (
                <button type="button" class="rpage__link" onClick={() => setHistoryOpen(true)}>
                  all {revisions.length}
                </button>
              ) : null}
            </>
          )}

          <div class="sp__sec" style={{ marginTop: '16px' }}>
            <span>Linked from</span>
          </div>
          {page.backlinks.length === 0 ? (
            <div class="sp__empty">Nothing links here yet.</div>
          ) : (
            page.backlinks.map((b) => (
              <button type="button" class="sp__row" key={b.slug} onClick={() => onOpenPage(b.slug)}>
                <span class="sp__row-top">
                  <span class="sp__row-name">{b.title}</span>
                </span>
              </button>
            ))
          )}

          <div class="sp__sec" style={{ marginTop: '16px' }}>
            <span>Links to</span>
          </div>
          {page.outlinks.length === 0 && page.unresolvedLinks.length === 0 ? (
            <div class="sp__empty">This page links nowhere yet.</div>
          ) : (
            <>
              {page.outlinks.map((o) => (
                <button type="button" class="sp__row" key={o.slug} onClick={() => onOpenPage(o.slug)}>
                  <span class="sp__row-top">
                    <span class="sp__row-name">{o.title}</span>
                  </span>
                </button>
              ))}
              {page.unresolvedLinks.map((slug) => (
                <div class="sp__row" key={slug} style={{ cursor: 'default' }}>
                  <span class="sp__row-top">
                    <span class="sp__row-name">{slug}</span>
                    <span class="sp__chip">not written</span>
                  </span>
                </div>
              ))}
            </>
          )}

          <div class="rpage__slug">
            <button type="button" class="rpage__link" onClick={() => void copySlug()}>
              Copy slug
            </button>
            {copyReceipt ? <span class="rpage__slug-receipt">{copyReceipt}</span> : null}
          </div>
        </div>
      </div>
    </div>
  );
}
